import { Company, ProductTier, User, UserMode } from '../../lib';
import { Permission } from '../permission';

type PermissionsProps = {
	[key: string]: (props: { user?: User; company?: Company }) => boolean;
};

function companyHasProductTier(company: Company | undefined, tiers: ProductTier[]): boolean {
	const companyTier = company?.product_tier ?? null;
	return companyTier != null && tiers.includes(companyTier);
}

// Orthogonal tier model: each permission explicitly defines which tiers have access
// TierAlpha (Team plan) has restricted access to top-down recognition features compared to TierThree (Organization plan)

export const PermissionValidations: PermissionsProps = {
	// One-on-Ones feature access - available on all go-forward plans: Free, Team (TierAlpha), Organization (TierThree)
	// Must match backend authorize_one_on_ones_access in tier_authorization.rb
	[Permission.CanAccessOneOnOnes]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierAlpha, ProductTier.TierThree]) && company?.feature_states?.one_on_ones !== false,

	// Performance Recaps feature access - available on all go-forward plans: Free, Team (TierAlpha), Organization (TierThree)
	[Permission.CanAccessRecaps]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierAlpha, ProductTier.TierThree]) && company?.feature_states?.recaps !== false,

	// Feedback feature access - available on all go-forward plans: Free, Team (TierAlpha), Organization (TierThree)
	[Permission.CanAccessAdvice]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierAlpha, ProductTier.TierThree]) && company?.feature_states?.advice !== false,

	[Permission.CanAccessGoals]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierAlpha, ProductTier.TierThree]) && company?.feature_states?.goals !== false,
	// home related validations
	[Permission.CanGiveRecognition]: ({ company, user }) =>
		// NOTE: can_give is true if company allows non-zero giving OR if user mode is normal, benefactor or bot
		user?.can_give === true && companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanAccessUserProfile]: ({ company }) => companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanAccessAnnouncements]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanAdministerAnnouncements]: ({ company, user }) =>
		(user?.global_admin === true || user?.announcements_admin === true) && companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanAccessAnalytics]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanAccessAdminOnlySettings]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanViewStrongerConnections]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanViewManagerGiving]: ({ company }) => companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanAccessUserSavedFeeds]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	// right sidebar related validations
	// user summary aka most recognized widget
	[Permission.CanViewUserSummary]: ({ company, user }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]) &&
		!(company?.hide_competitive_features && !user?.global_admin),

	// reward related validations
	[Permission.CanClaimPointsBoost]: ({ company, user }) =>
		user?.user_mode !== UserMode.Observer &&
		user?.user_mode !== UserMode.Receiver &&
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	// admin related validations
	[Permission.CanViewAdmin]: ({ user }) =>
		!!user?.admin ||
		!!user?.global_admin ||
		!!user?.awards_admin ||
		!!user?.rewards_admin ||
		!!user?.finance_admin ||
		!!user?.tech_admin ||
		!!user?.reports_admin ||
		!!user?.announcements_admin,

	[Permission.CanManageAwards]: ({ user }) => !!user?.admin || !!user?.global_admin || !!user?.awards_admin,

	// Intentionally excludes TierAlpha (Team): UI scope may differ from backend authorize_check_ins_access.
	[Permission.CanViewContinuousCheckIns]: ({ company }) => companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree]),

	[Permission.CanAccessUserManagement]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanManageUserImport]: ({ company }) =>
		!company?.user_management_integration_url && companyHasProductTier(company, [ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanManageTeamRequests]: ({ company }) =>
		!company?.hide_team_requests && !company?.user_management_integration_url && !company?.users_externally_synced,

	[Permission.CanViewExpandedReporting]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	// can manage show departments on analytics leaderboard checkbox > tier 1
	[Permission.CanManageShowDepartmentsOnLeaderboard]: ({ company }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	[Permission.CanManagePointsBoost]: ({ company }) => companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	// super admin related validation
	[Permission.CanViewSuperAdmin]: ({ user }) => !!user?.show_superadmin_feature,

	// admin programs validations
	[Permission.CanManagePeerToPeer]: ({ company }) => companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]),

	// Top-down recognition feature validations - available to all tiers except TierAlpha (Team plan)
	[Permission.CanAccessAwards]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanAccessNominations]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),
	// NOTE: CanAccessIncentives includes admin program visibility
	[Permission.CanAccessIncentives]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	// NOTE: CanAccessAwardsExperience / CanAccessIncentivesExperience are for user facing visibility
	// feature_states already takes into account the company's product tier so we dont check that here
	[Permission.CanAccessAwardsExperience]: ({ company }) => company?.feature_states?.awards !== false,

	[Permission.CanAccessIncentivesExperience]: ({ company }) => company?.feature_states?.incentives !== false,

	[Permission.CanAccessCelebrationsExperience]: ({ company }) => company?.feature_states?.celebrations !== false,

	[Permission.CanAccessExternalRecognition]: ({ company }) => company?.feature_states?.external_recognition === true,

	[Permission.CanAccessFlashPoints]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanCustomizeAdminSettings]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	[Permission.CanAccessCompanySSO]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	// Manager enablement validations
	// Pulse Checks / check-ins
	// feature_states already takes into account the company's product tier so we dont check that here
	[Permission.CanAccessCheckIn]: ({ company }) => company?.feature_states?.pulse_checks !== false,
	[Permission.CanAccessTeamHub]: ({ company, user }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]) && user?.is_manager === true,
	[Permission.CanAccessAdminCheckInControls]: ({ company, user }) =>
		companyHasProductTier(company, [ProductTier.TierFree, ProductTier.TierThree, ProductTier.TierAlpha]) && user?.global_admin === true,

	// Spotlight - Organization plan only
	// Must match backend authorize_spotlight_access in tier_authorization.rb
	[Permission.CanAccessSpotlight]: ({ company }) => companyHasProductTier(company, [ProductTier.TierThree]),

	// video related validations
	[Permission.CanManageVideoUploads]: ({ company }) => !company?.disable_video_uploads,
	[Permission.CanRecordVideo]: ({ company }) =>
		// If the company has allowed video uploading OR recording
		(!company?.disable_video_recordings || !company?.disable_video_uploads) &&
		// And they're not on the Appreciate package
		companyHasProductTier(company, [ProductTier.TierThree, ProductTier.TierAlpha]),
	[Permission.CanAccessVideoCarousel]: ({ company }) =>
		// If the company has allowed video uploading OR recording
		(!company?.disable_video_recordings || !company?.disable_video_uploads) &&
		// Video carousel is only available for TierThree, not TierAlpha (Team plan)
		companyHasProductTier(company, [ProductTier.TierThree]),
};
